import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  fetchAdminUnits,
  fetchAdminRooms,
  fetchAdminTenanciesAll,
  fetchAdminUnitCosts,
  fetchAdminCities,
  createAdminUnit,
  updateAdminUnit,
  createAdminUnitCost,
  deleteAdminUnitCost,
  geocodeAdminUnit,
  normalizeUnit,
  normalizeRoom,
} from "../../../api/adminData";
import { normalizeUnitTypeLabel } from "../../../utils/unitDisplayId";
import { getUnitMonthlyRunningCosts } from "../../../utils/adminUnitRunningCosts";
import { lookupSwissPlz } from "../../../data/swissPlzLookup";
import {
  parseOptionalMoneyChf,
  dateOnlyOrNull,
  computeLeaseStatusKey,
  strOrNull,
  numFieldStr,
  parseRoomsTotal,
  sumCoLivingRoomPricesChf,
  sumFirstNCoLivingRoomPricesChf,
  ensureCoLivingRoomRows,
  getTodayDateString,
  newModalCostRowId,
  makeDefaultModalCostRows,
  modalRowsFromApiCosts,
  parseModalCostAmount,
  buildValidModalCostRows,
  unitGeocodingStatusPresentation,
} from "./apartmentsHelpers";

const emptyForm = {
  place: "",
  zip: "",
  address: "",
  canton: "",
  type: "Apartment",
  rooms: "",
  occupiedRooms: 0,
  city_id: "",
  property_id: "",
  landlord_id: "",
  property_manager_id: "",
  tenantPriceMonthly: "",
  availableFrom: "",
  landlordDepositType: "",
  landlordDepositAmount: "",
  landlordDepositAnnualPremium: "",
  landlordDepositProvider: "",
  leaseType: "",
  leaseStartDate: "",
  leaseEndDate: "",
  noticeGivenDate: "",
  terminationEffectiveDate: "",
  returnedToLandlordDate: "",
  leaseStatus: "",
  leaseNotes: "",
  egid_awn: "",
};

export function useUnitFormModal({
  units,
  landlords,
  propertyManagers,
  setUnits,
  setRooms,
  setTenancies,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [createLandlordModalOpen, setCreateLandlordModalOpen] = useState(false);
  const [createPmModalOpen, setCreatePmModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [modalCostRows, setModalCostRows] = useState([]);
  const [coLivingRoomRows, setCoLivingRoomRows] = useState([]);
  const [unitAddrBusy, setUnitAddrBusy] = useState(false);
  const [unitCantonHint, setUnitCantonHint] = useState("");
  const [unitCantonLockedByPlz, setUnitCantonLockedByPlz] = useState(false);
  const [unitPlzNotFound, setUnitPlzNotFound] = useState(false);
  const [unitLastGeocoding, setUnitLastGeocoding] = useState(null);
  const [unitCoordSnapshot, setUnitCoordSnapshot] = useState(null);
  const [unitGeocodingRetrying, setUnitGeocodingRetrying] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [landlordFilter, setLandlordFilter] = useState("");
  const [propertyManagerFilter, setPropertyManagerFilter] = useState("");
  const [cities, setCities] = useState([]);
  const [citiesStatus, setCitiesStatus] = useState("idle");
  const citiesRequestedRef = useRef(false);

  const location = useLocation();
  const navigate = useNavigate();

  const normalizedUnitType = normalizeUnitTypeLabel(formData.type);
  const isCoLivingType = normalizedUnitType === "Co-Living";
  const parsedRoomsTotal = useMemo(
    () => parseRoomsTotal(formData.rooms),
    [formData.rooms]
  );

  const coLivingRowsForDisplay = useMemo(() => {
    if (!isCoLivingType || editingId || parsedRoomsTotal <= 0) return [];
    return ensureCoLivingRoomRows(parsedRoomsTotal, coLivingRoomRows);
  }, [isCoLivingType, editingId, parsedRoomsTotal, coLivingRoomRows]);

  const coLivingOccupiedClamped = useMemo(() => {
    if (!isCoLivingType) return 0;
    return 0;
  }, [isCoLivingType]);

  const coLivingFullOccupancyRevenue = useMemo(() => {
    if (!isCoLivingType) return 0;
    const rows = coLivingRowsForDisplay;
    if (rows.length > 0 && rows.length === parsedRoomsTotal && parsedRoomsTotal > 0) {
      return sumCoLivingRoomPricesChf(rows);
    }
    return 0;
  }, [
    isCoLivingType,
    coLivingRowsForDisplay,
    parsedRoomsTotal,
  ]);

  const derivedMonthlyTotalCosts = useMemo(() => {
    const unitLike = {
      landlordDepositType: formData.landlordDepositType,
      landlordDepositAnnualPremium: formData.landlordDepositAnnualPremium,
    };
    return getUnitMonthlyRunningCosts(unitLike, modalCostRows);
  }, [formData.landlordDepositType, formData.landlordDepositAnnualPremium, modalCostRows]);

  const derivedOneTimeCostsTotal = useMemo(() => {
    if (!Array.isArray(modalCostRows)) return 0;
    return modalCostRows.reduce((sum, r) => {
      const freq = String(r?.frequency || "monthly").trim().toLowerCase();
      if (freq !== "one_time") return sum;
      const amt = Number(r?.amount_chf);
      return sum + (Number.isFinite(amt) && amt > 0 ? amt : 0);
    }, 0);
  }, [modalCostRows]);

  const nextUnitId = useMemo(() => {
    const maxNumber = units.reduce((max, item) => {
      const uid = item.unitId || item.id || "";
      const parts = String(uid).split("-");
      const number = parseInt(parts[parts.length - 1] || "0", 10);
      return !isNaN(number) && number > max ? number : max;
    }, 0);
    return `FAH-U-${String(maxNumber + 1).padStart(4, "0")}`;
  }, [units]);

  const unitGeoLine = useMemo(
    () =>
      unitGeocodingStatusPresentation(
        unitLastGeocoding,
        unitCoordSnapshot,
        !editingId
      ),
    [unitLastGeocoding, unitCoordSnapshot, editingId]
  );

  // useLayoutEffect: must run before paint so room blocks exist before submit (Enter) in the same tick as the last rooms change.
  useLayoutEffect(() => {
    if (!isModalOpen || editingId) return;
    if (!isCoLivingType) {
      setCoLivingRoomRows([]);
      return;
    }
    const n = parsedRoomsTotal;
    if (n === 0) {
      setCoLivingRoomRows([]);
      return;
    }
    setCoLivingRoomRows((prev) => ensureCoLivingRoomRows(n, prev));
  }, [isModalOpen, editingId, isCoLivingType, parsedRoomsTotal]);

  // The city list is loaded the first time the form opens, once per page visit
  // — not on page load (whose request budget is pinned in the segments tests)
  // and not per unit. A failed load is retried on the next opening. It never
  // touches formData: a stored city_id survives a missing list, because the
  // select keeps an option for it and the payload sends formData, not the list.
  // The call sits inside the chain so that even a synchronous throw ends in the
  // same catch — the city is optional and must never take the form down.
  useEffect(() => {
    if (!isModalOpen || citiesRequestedRef.current) return;
    citiesRequestedRef.current = true;
    setCitiesStatus("loading");
    Promise.resolve()
      .then(() => fetchAdminCities())
      .then((data) => {
        setCities(Array.isArray(data) ? data : []);
        setCitiesStatus("loaded");
      })
      .catch(() => {
        citiesRequestedRef.current = false;
        setCitiesStatus("error");
      });
  }, [isModalOpen]);

  const filteredLandlordsForSelect = useMemo(() => {
    const q = landlordFilter.toLowerCase().trim();
    if (!q) return landlords;
    return landlords.filter((l) => {
      const blob = `${l.company_name || ""} ${l.contact_name || ""} ${l.email || ""}`.toLowerCase();
      return blob.includes(q);
    });
  }, [landlords, landlordFilter]);

  const filteredPropertyManagersForSelect = useMemo(() => {
    const q = propertyManagerFilter.toLowerCase().trim();
    if (!q) return propertyManagers;
    return propertyManagers.filter((p) => {
      const blob = `${p.name || ""} ${p.email || ""} ${p.phone || ""}`.toLowerCase();
      return blob.includes(q);
    });
  }, [propertyManagers, propertyManagerFilter]);

  function handleOpenCreateModal() {
    setEditingId(null);
    setFormData(emptyForm);
    setModalCostRows(makeDefaultModalCostRows());
    setCoLivingRoomRows([]);
    setLandlordFilter("");
    setPropertyManagerFilter("");
    setUnitAddrBusy(false);
    setUnitCantonHint("");
    setUnitCantonLockedByPlz(false);
    setUnitPlzNotFound(false);
    setUnitLastGeocoding(null);
    setUnitCoordSnapshot(null);
    setUnitGeocodingRetrying(false);
    setIsModalOpen(true);
  }

  const handleOpenEditModal = useCallback(async (unit) => {
    setEditingId(unit.id);
    setLandlordFilter("");
    setPropertyManagerFilter("");
    setFormData({
      place: unit.place,
      zip: unit.zip != null && unit.zip !== "" ? String(unit.zip) : "",
      address: unit.address,
      canton: "",
      type: unit.type,
      rooms: unit.rooms,
      occupiedRooms: unit.occupiedRooms || 0,
      // The stored reference only. A unit without one stays empty until a city
      // is picked — never looked up from the free-text `place`.
      city_id:
        unit.city_id != null && unit.city_id !== ""
          ? String(unit.city_id)
          : "",
      property_id: unit.property_id || "",
      landlord_id:
        unit.landlord_id != null && unit.landlord_id !== ""
          ? String(unit.landlord_id)
          : "",
      property_manager_id:
        unit.property_manager_id != null && unit.property_manager_id !== ""
          ? String(unit.property_manager_id)
          : "",
      tenantPriceMonthly: numFieldStr(unit.tenantPriceMonthly),
      availableFrom: numFieldStr(unit.availableFrom).slice(0, 10),
      landlordDepositType: String(unit.landlordDepositType || "").trim(),
      landlordDepositAmount: numFieldStr(unit.landlordDepositAmount),
      landlordDepositAnnualPremium: numFieldStr(unit.landlordDepositAnnualPremium),
      landlordDepositProvider: String(unit.landlordDepositProvider || "").trim(),
      leaseType: String(unit.leaseType ?? "").trim(),
      leaseStartDate: numFieldStr(unit.leaseStartDate).slice(0, 10),
      leaseEndDate: numFieldStr(unit.leaseEndDate).slice(0, 10),
      noticeGivenDate: numFieldStr(unit.noticeGivenDate).slice(0, 10),
      terminationEffectiveDate: numFieldStr(unit.terminationEffectiveDate).slice(
        0,
        10
      ),
      returnedToLandlordDate: numFieldStr(unit.returnedToLandlordDate).slice(0, 10),
      leaseStatus: String(unit.leaseStatus ?? "").trim(),
      leaseNotes: unit.leaseNotes != null ? String(unit.leaseNotes) : "",
      egid_awn: unit.egid_awn != null ? String(unit.egid_awn) : "",
    });
    setSaveError("");
    setUnitAddrBusy(false);
    setUnitCantonHint("");
    setUnitCantonLockedByPlz(false);
    setUnitPlzNotFound(false);
    setUnitLastGeocoding(null);
    setUnitCoordSnapshot(
      unit.latitude != null && unit.longitude != null
        ? { lat: unit.latitude, lng: unit.longitude }
        : { lat: null, lng: null }
    );
    setUnitGeocodingRetrying(false);
    let costRows = makeDefaultModalCostRows();
    try {
      const costs = await fetchAdminUnitCosts(unit.id);
      costRows = modalRowsFromApiCosts(costs);
    } catch {
      costRows = makeDefaultModalCostRows();
    }
    setModalCostRows(costRows);
    setIsModalOpen(true);
  }, []);

  useEffect(() => {
    const raw = location.state?.editUnitId;
    if (raw == null || raw === "") return;
    if (units.length === 0) return;
    const id = String(raw);
    const unit = units.find(
      (u) => String(u.id) === id || String(u.unitId) === id
    );
    if (unit) {
      handleOpenEditModal(unit);
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [units, location.state, location.pathname, navigate, handleOpenEditModal]);

  function handleCloseModal() {
    setIsModalOpen(false);
    setCreateLandlordModalOpen(false);
    setCreatePmModalOpen(false);
    setEditingId(null);
    setFormData(emptyForm);
    setModalCostRows([]);
    setCoLivingRoomRows([]);
    setLandlordFilter("");
    setPropertyManagerFilter("");
    setUnitAddrBusy(false);
    setUnitCantonHint("");
    setUnitCantonLockedByPlz(false);
    setUnitPlzNotFound(false);
    setUnitLastGeocoding(null);
    setUnitCoordSnapshot(null);
    setUnitGeocodingRetrying(false);
  }

  function addModalCostRow() {
    setModalCostRows((prev) => [
      ...prev,
      {
        id: newModalCostRowId(),
        cost_type: "",
        custom_type: "",
        amount_chf: "",
        frequency: "monthly",
      },
    ]);
  }

  const handleUnitPostalCodeChange = (e) => {
    const next = e.target.value;
    const plz = next.trim();
    if (!/^\d{4}$/.test(plz)) {
      setUnitCantonLockedByPlz(false);
      setUnitPlzNotFound(false);
      setFormData((f) => ({ ...f, zip: next }));
      return;
    }
    const hit = lookupSwissPlz(plz);
    if (hit) {
      setFormData((f) => ({
        ...f,
        zip: next,
        place: hit.city,
        canton: hit.canton,
      }));
      setUnitCantonLockedByPlz(true);
      setUnitPlzNotFound(false);
    } else {
      setFormData((f) => ({ ...f, zip: next }));
      setUnitCantonLockedByPlz(false);
      setUnitPlzNotFound(true);
    }
  };

  function removeModalCostRow(rowId) {
    setModalCostRows((prev) => prev.filter((r) => r.id !== rowId));
  }

  function updateModalCostRow(rowId, patch) {
    setModalCostRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, ...patch } : r))
    );
  }

  function handleCoLivingRoomChange(index, field, rawValue) {
    setCoLivingRoomRows((prev) => {
      const base = ensureCoLivingRoomRows(parsedRoomsTotal, prev);
      const next = [...base];
      if (index < 0 || index >= next.length) return prev;
      next[index] = { ...next[index], [field]: rawValue };
      return next;
    });
  }

  function handleChange(event) {
    const { name, value } = event.target;

    let nextValue = value;

    if (name === "occupiedRooms") {
      const totalRooms = parseRoomsTotal(formData.rooms);
      let occupied = Number(value);
      if (Number.isNaN(occupied)) occupied = 0;
      occupied = Math.floor(occupied);
      if (occupied < 0) occupied = 0;
      if (totalRooms > 0 && occupied > totalRooms) occupied = totalRooms;
      nextValue = occupied;
    }

    if (name === "type" && value === "Apartment") {
      setFormData((prev) => ({
        ...prev,
        type: value,
        occupiedRooms: 0,
      }));
      return;
    }

    if (name === "type" && value === "Co-Living") {
      setFormData((prev) => ({
        ...prev,
        type: value,
        occupiedRooms: 0,
      }));
      return;
    }

    if (name === "landlordDepositType" && value !== "insurance") {
      // Provider joins the premium here: both are insurance-only. This is a
      // convenience so the operator sees the field empty at once — the server
      // clears the stored value regardless of what the form sends.
      setFormData((prev) => ({
        ...prev,
        landlordDepositType: value,
        landlordDepositAnnualPremium: "",
        landlordDepositProvider: "",
      }));
      return;
    }

    if (name === "leaseType" && value !== "fixed_term") {
      setFormData((prev) => ({
        ...prev,
        leaseType: value,
        leaseEndDate: "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaveError("");

    const validCostRows = buildValidModalCostRows(modalCostRows);
    if (validCostRows.length === 0) {
      setSaveError(
        "Mindestens eine Kosten-Zeile mit Kostenart, Frequenz und gültigem Betrag ist erforderlich."
      );
      return;
    }

    const coLivingRowsForSubmit =
      !editingId && isCoLivingType && parsedRoomsTotal > 0
        ? ensureCoLivingRoomRows(parsedRoomsTotal, coLivingRoomRows)
        : coLivingRoomRows;

    if (!editingId && isCoLivingType) {
      const n = parsedRoomsTotal;
      if (n > 0) {
        if (coLivingRowsForSubmit.length !== n) {
          setSaveError(
            "Bei Co-Living muss die Anzahl Zimmer mit den ausgefüllten Zimmerzeilen übereinstimmen."
          );
          return;
        }
        for (let i = 0; i < n; i++) {
          const row = coLivingRowsForSubmit[i];
          if (!row || !String(row.name || "").trim()) {
            setSaveError(`Zimmer ${i + 1}: Name ist erforderlich.`);
            return;
          }
          if (row.price === "" || row.price === null || row.price === undefined) {
            setSaveError(`Zimmer ${i + 1}: Preis ist erforderlich.`);
            return;
          }
          const prn = Number(String(row.price).replace(",", "."));
          if (Number.isNaN(prn) || prn < 0) {
            setSaveError(`Zimmer ${i + 1}: Ungültiger Preis.`);
            return;
          }
          if (row.floor !== "" && row.floor != null) {
            const fl = Number(String(row.floor).replace(",", "."));
            if (Number.isNaN(fl) || fl < 0) {
              setSaveError(`Zimmer ${i + 1}: Ungültige Etage.`);
              return;
            }
          }
          if (row.size_m2 !== "" && row.size_m2 != null) {
            const sm = Number(String(row.size_m2).replace(",", "."));
            if (Number.isNaN(sm) || sm < 0) {
              setSaveError(`Zimmer ${i + 1}: Ungültige Fläche (m²).`);
              return;
            }
          }
        }
      }
    }

    setSaving(true);

    const computedLeaseStatus = computeLeaseStatusKey(formData) || null;
    const persistedUnitFields = {
      // Step 5.1 scope correction: tenant_price_monthly_chf restored
      // as planning-revenue (see models.py). Other cost fields remain
      // in unit_costs.
      tenant_price_monthly_chf: Number(formData.tenantPriceMonthly || 0),
      available_from: dateOnlyOrNull(formData.availableFrom),
      occupied_rooms: Math.max(0, Math.floor(Number(formData.occupiedRooms) || 0)),
      postal_code: String(formData.zip || "").trim() || null,
      landlord_deposit_type: String(formData.landlordDepositType || "").trim() || null,
      landlord_deposit_amount: parseOptionalMoneyChf(formData.landlordDepositAmount),
      landlord_deposit_annual_premium: parseOptionalMoneyChf(
        formData.landlordDepositAnnualPremium
      ),
      // The canonical lowercase value, never the display label.
      landlord_deposit_provider:
        String(formData.landlordDepositProvider || "").trim() || null,
      lease_type: strOrNull(formData.leaseType),
      lease_start_date: dateOnlyOrNull(formData.leaseStartDate),
      lease_end_date:
        formData.leaseType === "fixed_term"
          ? dateOnlyOrNull(formData.leaseEndDate)
          : null,
      notice_given_date: dateOnlyOrNull(formData.noticeGivenDate),
      termination_effective_date: dateOnlyOrNull(
        formData.terminationEffectiveDate
      ),
      returned_to_landlord_date: dateOnlyOrNull(formData.returnedToLandlordDate),
      lease_status: computedLeaseStatus,
      lease_notes: strOrNull(formData.leaseNotes),
      egid_awn: strOrNull(formData.egid_awn),
    };

    const baseUnitPayload = {
      title: (formData.place || formData.address || "Unit").trim() || "Unit",
      address: (formData.address || "").trim() || "",
      city: (formData.place || "").trim() || "",
      city_id: (formData.city_id || "").trim() || null,
      type: normalizedUnitType || null,
      rooms: parsedRoomsTotal,
      property_id: (formData.property_id || "").trim() || null,
      landlord_id: (formData.landlord_id || "").trim() || null,
      property_manager_id: (formData.property_manager_id || "").trim() || null,
    };

    const apiPayload = {
      ...baseUnitPayload,
      ...persistedUnitFields,
    };

    if (!editingId && isCoLivingType) {
      const n = parsedRoomsTotal;
      if (n > 0) {
        apiPayload.co_living_rooms = coLivingRowsForSubmit.map((row) => {
          const floorRaw = row.floor;
          const floor =
            floorRaw === "" || floorRaw == null
              ? null
              : Math.round(Number(String(floorRaw).replace(",", ".")));
          const sizeRaw = row.size_m2;
          const size_m2 =
            sizeRaw === "" || sizeRaw == null
              ? null
              : Number(String(sizeRaw).replace(",", "."));
          return {
            name: String(row.name).trim(),
            price: Math.round(Number(String(row.price).replace(",", "."))),
            floor,
            size_m2,
            status: "Frei",
          };
        });
      }
    }

    try {
      if (editingId) {
        const saved = await updateAdminUnit(editingId, {
          ...baseUnitPayload,
          ...persistedUnitFields,
        });
        if (saved) {
          setUnitLastGeocoding(saved.geocoding ?? null);
          setUnitCoordSnapshot(
            saved.latitude != null && saved.longitude != null
              ? { lat: saved.latitude, lng: saved.longitude }
              : { lat: null, lng: null }
          );
        }
        const g = saved && saved.geocoding;
        if (g && g.status !== "ok" && g.reason && g.reason !== "unchanged") {
          const reason = g.reason;
          let msg =
            "Unit gespeichert, aber Koordinaten konnten nicht automatisch ermittelt werden.";
          if (reason === "incomplete_address") {
            msg =
              "Unit gespeichert, aber die Adresse war für die automatische Kartenposition unvollständig.";
          } else if (reason === "provider_unavailable") {
            msg =
              "Unit gespeichert, aber Koordinaten konnten nicht automatisch ermittelt werden (Geocoding nicht konfiguriert).";
          }
          toast.warning(msg);
        }
        const existing = await fetchAdminUnitCosts(editingId);
        for (const c of existing) {
          await deleteAdminUnitCost(editingId, c.id);
        }
        for (const row of validCostRows) {
          await createAdminUnitCost(editingId, {
            cost_type: row.cost_type,
            amount_chf: row.amount_chf,
            frequency: row.frequency,
          });
        }
      } else {
        const created = await createAdminUnit(apiPayload);
        const g = created && created.geocoding;
        if (g && g.status !== "ok" && g.reason && g.reason !== "unchanged") {
          const reason = g.reason;
          let msg =
            "Unit gespeichert, aber Koordinaten konnten nicht automatisch ermittelt werden.";
          if (reason === "incomplete_address") {
            msg =
              "Unit gespeichert, aber die Adresse war für die automatische Kartenposition unvollständig.";
          } else if (reason === "provider_unavailable") {
            msg =
              "Unit gespeichert, aber Koordinaten konnten nicht automatisch ermittelt werden (Geocoding nicht konfiguriert).";
          }
          toast.warning(msg);
        }
        const newId = created?.id || created?.unitId;
        if (!newId) {
          throw new Error("Unit konnte nicht gespeichert werden.");
        }
        for (const row of validCostRows) {
          await createAdminUnitCost(newId, {
            cost_type: row.cost_type,
            amount_chf: row.amount_chf,
            frequency: row.frequency,
          });
        }
      }

      const [unitsData, roomsData, tenanciesData] = await Promise.all([
        fetchAdminUnits(),
        fetchAdminRooms(),
        fetchAdminTenanciesAll(),
      ]);
      setUnits(Array.isArray(unitsData) ? unitsData.map(normalizeUnit) : []);
      setRooms(Array.isArray(roomsData) ? roomsData.map(normalizeRoom) : []);
      setTenancies(Array.isArray(tenanciesData) ? tenanciesData : []);
      handleCloseModal();
    } catch (e) {
      const msg =
        (typeof e === "string" && e) ||
        (e && typeof e.message === "string" && e.message) ||
        (e != null && String(e)) ||
        "Speichern fehlgeschlagen.";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  }

  const handleRetryUnitGeocode = useCallback(() => {
    if (!editingId || unitGeocodingRetrying) return;
    setUnitGeocodingRetrying(true);
    geocodeAdminUnit(editingId)
      .then((data) => {
        setUnitLastGeocoding(data.geocoding ?? null);
        setUnitCoordSnapshot(
          data.latitude != null && data.longitude != null
            ? { lat: data.latitude, lng: data.longitude }
            : { lat: null, lng: null }
        );
        const g = data.geocoding;
        if (g && g.status === "ok") {
          toast.success("Koordinaten erfolgreich aktualisiert.");
        } else if (g && g.reason === "incomplete_address") {
          toast.warning("Adresse unvollständig – Koordinaten konnten nicht ermittelt werden.");
        } else if (g && g.reason === "provider_unavailable") {
          toast.warning("Geocoding nicht verfügbar.");
        } else {
          toast.warning("Koordinaten konnten nicht automatisch ermittelt werden.");
        }
        return Promise.all([
          fetchAdminUnits(),
          fetchAdminRooms(),
          fetchAdminTenanciesAll(),
        ]).then(([unitsData, roomsData, tenanciesData]) => {
          setUnits(Array.isArray(unitsData) ? unitsData.map(normalizeUnit) : []);
          setRooms(Array.isArray(roomsData) ? roomsData.map(normalizeRoom) : []);
          setTenancies(Array.isArray(tenanciesData) ? tenanciesData : []);
        });
      })
      .catch((e) => toast.error(e.message || "Geocoding fehlgeschlagen."))
      .finally(() => setUnitGeocodingRetrying(false));
  }, [editingId, unitGeocodingRetrying, setUnits, setRooms, setTenancies]);

  const formLeaseStarted =
    !formData.leaseStartDate ||
    formData.leaseStartDate <= getTodayDateString();

  const formRunningMonthlyCosts = useMemo(() => {
    if (!formLeaseStarted) return 0;
    return modalCostRows.reduce((sum, row) => {
      const n = parseModalCostAmount(row.amount_chf);
      return sum + (n != null ? n : 0);
    }, 0);
  }, [formLeaseStarted, modalCostRows]);

  const currentApartmentProfit =
    Number(formData.tenantPriceMonthly || 0) - formRunningMonthlyCosts;

  const currentFreeRooms = isCoLivingType
    ? Math.max(parsedRoomsTotal - coLivingOccupiedClamped, 0)
    : "-";

  const currentCoLivingRevenue =
    isCoLivingType && parsedRoomsTotal > 0 && formLeaseStarted
      ? coLivingRowsForDisplay.length > 0 &&
        coLivingRowsForDisplay.length === parsedRoomsTotal
        ? sumFirstNCoLivingRoomPricesChf(
            coLivingRowsForDisplay,
            coLivingOccupiedClamped
          )
        : (coLivingFullOccupancyRevenue / parsedRoomsTotal) *
          coLivingOccupiedClamped
      : 0;

  const currentCoLivingVacancy =
    isCoLivingType && formLeaseStarted
      ? coLivingFullOccupancyRevenue - currentCoLivingRevenue
      : 0;

  const currentCoLivingProfit =
    currentCoLivingRevenue - formRunningMonthlyCosts;

  return {
    // state
    isModalOpen,
    setIsModalOpen,
    createLandlordModalOpen,
    setCreateLandlordModalOpen,
    createPmModalOpen,
    setCreatePmModalOpen,
    editingId,
    formData,
    setFormData,
    modalCostRows,
    coLivingRoomRows,
    unitAddrBusy,
    setUnitAddrBusy,
    unitCantonHint,
    setUnitCantonHint,
    unitCantonLockedByPlz,
    unitPlzNotFound,
    unitGeocodingRetrying,
    saveError,
    saving,
    landlordFilter,
    setLandlordFilter,
    propertyManagerFilter,
    setPropertyManagerFilter,
    cities,
    citiesStatus,

    // derived / memos
    isCoLivingType,
    parsedRoomsTotal,
    coLivingRowsForDisplay,
    coLivingFullOccupancyRevenue,
    derivedMonthlyTotalCosts,
    derivedOneTimeCostsTotal,
    nextUnitId,
    unitGeoLine,
    filteredLandlordsForSelect,
    filteredPropertyManagersForSelect,
    formLeaseStarted,
    currentApartmentProfit,
    currentCoLivingRevenue,
    currentCoLivingVacancy,
    currentCoLivingProfit,
    currentFreeRooms,

    // handlers
    handleOpenCreateModal,
    handleOpenEditModal,
    handleCloseModal,
    handleChange,
    handleSubmit,
    handleUnitPostalCodeChange,
    handleRetryUnitGeocode,
    handleCoLivingRoomChange,
    addModalCostRow,
    removeModalCostRow,
    updateModalCostRow,
  };
}
